import { ExternalLink, Sparkles, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import type { CoachHealthWorkspacePresentation } from "@/lib/coachHealth/coachHealthPresentationContracts";

interface CoachHealthReflectionWorkspaceProps {
  readonly workspaceId: string;
  readonly presentation: CoachHealthWorkspacePresentation;
  readonly onClose: () => void;
  readonly onRetry?: () => void;
}

export function CoachHealthReflectionWorkspace({
  workspaceId,
  presentation,
  onClose,
  onRetry,
}: CoachHealthReflectionWorkspaceProps) {
  return (
    <div className="min-w-0 max-w-full overflow-x-hidden bg-background">
      <header className="sticky top-0 z-10 flex min-w-0 items-center justify-between gap-3 bg-primary px-4 py-3 text-primary-foreground">
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-foreground/15">
            <Sparkles className="h-5 w-5" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold">Coach Veera</p>
            <p className="truncate text-xs text-primary-foreground/75">{presentation.audienceLabel}</p>
          </div>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-11 w-11 shrink-0 text-primary-foreground hover:bg-primary-foreground/15 hover:text-primary-foreground"
          aria-label="Close practice reflection"
          onClick={onClose}
        >
          <X className="h-5 w-5" aria-hidden="true" />
        </Button>
      </header>

      <div className="min-w-0 space-y-6 p-4 sm:p-6">
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
            {presentation.privacyLabel}
          </p>
          <h2 id={`${workspaceId}-title`} className="text-2xl font-semibold text-foreground">
            {presentation.heading}
          </h2>
          <p className="text-sm text-muted-foreground">{presentation.metricStatement}</p>
        </div>

        <section aria-label="Reflection context" className="space-y-3">
          {presentation.dimensions.map((dimension) => (
            <article key={dimension.key} className="min-w-0 rounded-xl border border-border/70 bg-card p-4">
              <div className="flex min-w-0 flex-wrap items-start justify-between gap-2">
                <h3 className="font-semibold text-foreground">{dimension.label}</h3>
                <span className="rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground">
                  {dimension.stateLabel}
                </span>
              </div>
              <p className="mt-2 break-words text-sm leading-6 text-muted-foreground">
                {dimension.explanation}
              </p>
            </article>
          ))}
        </section>

        <section aria-label="Recorded sources" className="space-y-3">
          <div>
            <h3 className="font-semibold text-foreground">Recorded sources</h3>
            <p className="mt-1 text-xs text-muted-foreground">{presentation.leaderPerspectiveLabel}</p>
          </div>
          {presentation.evidence.map((evidence, index) => (
            <article
              key={`${evidence.sourceLabel}-${evidence.timestampLabel}-${index}`}
              className="min-w-0 rounded-xl border border-border/70 bg-card p-4"
            >
              <p className="font-medium text-foreground">{evidence.sourceLabel}</p>
              <p className="mt-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                Leader profile
              </p>
              <p className="mt-1 text-sm text-muted-foreground">{evidence.leaderLabel}</p>
              <p className="mt-1 break-words text-xs text-muted-foreground">{evidence.timestampLabel}</p>
              {evidence.route ? (
                <a
                  href={evidence.route}
                  aria-label={`View ${evidence.sourceLabel} source`}
                  className="mt-3 inline-flex min-h-11 items-center gap-2 rounded-md border border-input px-3 py-2 text-sm font-medium text-foreground hover:bg-accent"
                >
                  View source
                  <ExternalLink className="h-4 w-4" aria-hidden="true" />
                </a>
              ) : evidence.unavailableCopy ? (
                <p className="mt-3 text-xs text-muted-foreground">{evidence.unavailableCopy}</p>
              ) : null}
            </article>
          ))}
        </section>

        {presentation.reflectionQuestion ? (
          <section className="rounded-xl bg-primary/5 p-4" aria-label="Reflection question">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-primary">Notice next</p>
            <p className="mt-2 text-sm font-medium leading-6 text-foreground">
              {presentation.reflectionQuestion}
            </p>
          </section>
        ) : null}

        {presentation.retryLabel && onRetry ? (
          <Button type="button" variant="outline" className="min-h-11" onClick={onRetry}>
            {presentation.retryLabel}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
